export interface TypeaheadDataEvent<T = unknown> {
  data: T[];
  id?: string;
  type?: string;
  /** Search scope of the picker this reply answers (#34282). */
  scope?: string;
}

/**
 * The id space a typeahead's values live in (WI 34806). Only Sparta-backed
 * typeaheads (`options.pointToSparta`) declare one; everything else is the
 * default space, which for facility and customer pickers is Viewpoint.
 */
export type TypeaheadDataSource = 'sparta';

/** A filterTypeahead request on the event bus. */
export interface TypeaheadFilterRequest {
  id: string;
  query?: string;
  initWithQuery?: string;
  /** Set by a picker modal so only the typeahead that opened it answers (#34282). */
  scope?: string;
  /** Id space of `initWithQuery`; unset means the default (non-Sparta) space (WI 34806). */
  source?: TypeaheadDataSource;
}

/** The data source a typeahead declares from its options (WI 34806). */
export function typeaheadDataSource(
  options: { pointToSparta?: boolean } | null | undefined
): TypeaheadDataSource | undefined {
  return options?.pointToSparta ? 'sparta' : undefined;
}

export function createTypeaheadDataEvent<T>(
  id: string | undefined,
  data: T[],
  type?: string,
  scope?: string
): TypeaheadDataEvent<T> {
  return {
    id,
    type,
    data,
    ...(scope ? { scope } : {}),
  };
}

/**
 * `scope`, when the consumer has one, must match exactly: a picker opened by one
 * typeahead ignores results from any other typeahead with the same
 * formControlName, including unsolicited preloads that carry no scope.
 */
export function matchesTypeaheadDataEvent(
  payload: TypeaheadDataEvent<unknown> | null | undefined,
  id: string | undefined,
  type?: string,
  scope?: string
): boolean {
  if (!payload || !id || payload.id !== id) {
    return false;
  }

  if (type && payload.type && payload.type !== type) {
    return false;
  }

  if (scope && payload.scope !== scope) {
    return false;
  }

  return true;
}

/**
 * #34282: formControlName is not unique across live pages (the HSE Toolbox tabs
 * keep a 'facility' filter alive next to the Sign In Sheet 'facility' picker),
 * so a scoped request is answered only by the typeahead that owns the scope.
 * An unscoped request (page filters re-initialising a control) keeps matching
 * by formControlName.
 */
export function matchesTypeaheadFilterRequest(
  request: TypeaheadFilterRequest | null | undefined,
  id: string | undefined,
  ownScope: string,
  ownSource?: TypeaheadDataSource
): boolean {
  if (!request || !id || request.id !== id) {
    return false;
  }
  if (request.scope) {
    return request.scope === ownScope;
  }
  // WI 34806: an unscoped re-init carries an id from its sender's data source.
  // Coach Forms, Review Forms and My Submissions re-broadcast a Viewpoint
  // facility KeyID as 'facility'; the Sign In Sheet's Sparta picker matched
  // that number as a Sparta ParentFacilityID (274: Viewpoint Carson, Sparta
  // Freeport) and a later save wrote the wrong site. Ids never cross sources.
  return (request.source ?? null) === (ownSource ?? null);
}

/** A typeahead as seen by `isOwnFilterRequest`. */
export interface TypeaheadFilterOwner {
  config?: { formControlName?: string; options?: { pointToSparta?: boolean } };
  searchScope?: string;
}

/** `matchesTypeaheadFilterRequest` for a typeahead instance. */
export function isOwnFilterRequest(
  request: TypeaheadFilterRequest | null | undefined,
  owner: TypeaheadFilterOwner
): boolean {
  return matchesTypeaheadFilterRequest(
    request,
    owner.config?.formControlName,
    owner.searchScope ?? '',
    typeaheadDataSource(owner.config?.options)
  );
}

let typeaheadSearchScopeSeq = 0;

/** A process-unique scope token for one typeahead instance. */
export function nextTypeaheadSearchScope(): string {
  typeaheadSearchScopeSeq += 1;
  return `typeahead-${typeaheadSearchScopeSeq}`;
}
